/**
 * Query Keys Factory
 * Centralized query key management for type-safe cache invalidation
 */

export const queryKeys = {
  health: {
    all: ['health'] as const,
    check: () => [...queryKeys.health.all, 'check'] as const,
  },
  readiness: {
    all: ['readiness'] as const,
    check: () => [...queryKeys.readiness.all, 'check'] as const,
  },
  spreadCheckpoint: {
    all: ['spreadCheckpoint'] as const,
    byId: (spreadId: string, params?: { decodeElements?: boolean; decodeElementsLimit?: number }) =>
      [
        ...queryKeys.spreadCheckpoint.all,
        'byId',
        spreadId,
        {
          decodeElements: params?.decodeElements ?? true,
          decodeElementsLimit: params?.decodeElementsLimit ?? null,
        },
      ] as const,
  },
  spreads: {
    all: ['spreads'] as const,
  },
  dictionary: {
    all: ['dictionary'] as const,
  },
  fontFamilies: {
    all: ['fontFamilies'] as const,
    used: (spreadId?: string) => [...queryKeys.fontFamilies.all, 'used', spreadId ?? null] as const,
  },
  photos: {
    all: ['photos'] as const,
    topics: () => [...queryKeys.photos.all, 'topics'] as const,
    tags: () => [...queryKeys.photos.all, 'tags'] as const,
    albums: () => [...queryKeys.photos.all, 'albums'] as const,
    /** spreadId is only part of the cache key for Recommend for spread. */
    albumPhotosSpreadScope: (selectionKey: string | null, spreadId?: string | null) =>
      selectionKey === 'curated:recommended' ? (spreadId ?? null) : null,
    albumPhotos: (selectionKey: string | null, spreadId?: string | null) =>
      [
        ...queryKeys.photos.all,
        'albumPhotos',
        selectionKey,
        queryKeys.photos.albumPhotosSpreadScope(selectionKey, spreadId),
      ] as const,
    candidDetail: (candidId: string | null) =>
      [...queryKeys.photos.all, 'candidDetail', candidId] as const,
    candidAutoTagging: (candidId: string | null) =>
      [...queryKeys.photos.all, 'candidAutoTagging', candidId] as const,
    searchPhotos: (filename: string) => [...queryKeys.photos.all, 'search', filename] as const,
    effectsCatalog: () => [...queryKeys.photos.all, 'effectsCatalog'] as const,
    pez: (pezId: string | null) => [...queryKeys.photos.all, 'pez', pezId] as const,
  },
  backgrounds: {
    all: ['backgrounds'] as const,
    list: (params: { doublePage: boolean; fourColor: boolean; backgroundType: string | null }) =>
      [...queryKeys.backgrounds.all, 'list', params] as const,
    keywords: () => [...queryKeys.backgrounds.all, 'keywords'] as const,
  },
  clikArt: {
    all: ['clikArt'] as const,
    search: (params: { displayName: string; keywordIds: number[] }) =>
      [...queryKeys.clikArt.all, 'search', params] as const,
    keywords: () => [...queryKeys.clikArt.all, 'keywords'] as const,
    categories: () => [...queryKeys.clikArt.all, 'categories'] as const,
    placedIds: () => [...queryKeys.clikArt.all, 'placedIds'] as const,
  },
  colors: {
    all: ['colors'] as const,
    catalog: () => [...queryKeys.colors.all, 'catalog'] as const,
    styles: () => [...queryKeys.colors.all, 'styles'] as const,
  },
  templates: {
    all: ['templates'] as const,
    /** `selection` is `TemplateCategorySelection` (`lib/templates/template-category-selection.ts`)
     *  — inlined here rather than imported, matching this file's existing convention of not
     *  pulling in domain types. */
    list: (
      selection:
        { type: 'curated'; key: 'all' | 'custom' } | { type: 'category'; categoryId: number }
    ) => [...queryKeys.templates.all, 'list', selection] as const,
    keywords: () => [...queryKeys.templates.all, 'keywords'] as const,
    categories: () => [...queryKeys.templates.all, 'categories'] as const,
  },
  portraits: {
    all: ['portraits'] as const,
    /** Every roster query, whatever its filters (what a placement refresh invalidates). */
    allStudents: () => [...queryKeys.portraits.all, 'students'] as const,
    students: (params: {
      searchName: string
      gradeId: string
      homeroomId: string
      customId: string
    }) => [...queryKeys.portraits.all, 'students', params] as const,
    grades: () => [...queryKeys.portraits.all, 'grades'] as const,
    homerooms: () => [...queryKeys.portraits.all, 'homerooms'] as const,
    customCategories: () => [...queryKeys.portraits.all, 'custom-categories'] as const,
    detail: (portraitId: string | null) =>
      [...queryKeys.portraits.all, 'detail', portraitId] as const,
  },
} as const
